import { Box, Flex, Image, Text } from "@chakra-ui/react";
import { shortenAddress } from "@/utils";
import { accAdd, formatAmountWithFixed } from "@/utils/formatBalance";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { usdcInfo } from "@/constants/tokens";
import { Order, OrderType, Status } from "@/api/order";
import { avatarImg, consumerEmoji, unknownEmoji } from "@/utils/avatars";
import { useNavigate } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import NoDataIcon from "./assets/noData.png";
import CloseIcon from "@/assets/close.png";
import { BottomSheet, BottomSheetRef } from "react-spring-bottom-sheet";
import "react-spring-bottom-sheet/dist/style.css";
import "./bottomStyle.css";
import { defaultSnapProps } from "react-spring-bottom-sheet/dist/types";
import { LogsButton } from "@/components/Button";
import { useCurrencyExchange } from "@/hooks/useCurrencyExchange";
dayjs.extend(relativeTime);

const Activity = ({
  list,
  showBalance,
}: {
  list: Order[];
  showBalance: boolean;
}) => {
  const navigate = useNavigate();
  const sheetRef = useRef<BottomSheetRef>(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayOpacity, setOverlayOpacity] = useState(0);
  const [showDetail, setShowDetail] = useState(false);
  const [detail, setDetail] = useState<Order | null>(null);
  const { usdToCurrency, currencyUnit } = useCurrencyExchange();

  const handleGoReceipt = async (order: Order) => {
    sheetRef.current?.snapTo((state: defaultSnapProps) => {
      console.log(state, "state");
      return state.snapPoints[1];
    });
    setTimeout(() => {
      setShowDetail(true);
      setDetail(order);
    }, 100);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const sheet = sheetRef.current;
      if (!sheet) return;

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const height = entry.contentRect.height;
          const maxHeight = window.innerHeight;
          const quarterHeight = Math.ceil((maxHeight / 10) * 3) + 10;

          if (height <= quarterHeight) {
            setOverlayOpacity(0);
            setShowDetail(false);
          } else if (height >= quarterHeight + 50) {
            setOverlayOpacity(1);
          } else {
            const opacity = (height - quarterHeight) / 50;
            setOverlayOpacity(opacity);
          }
          setShowOverlay(height > quarterHeight);
        }
      });

      const element = document.querySelector(
        'div[role="dialog"][aria-modal="true"]'
      );
      if (element) {
        resizeObserver.observe(element);
      }

      return () => {
        resizeObserver.disconnect();
      };
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <Box>
      {showOverlay && (
        <Box
          onClick={() => {
            setShowDetail(false);
            sheetRef.current?.snapTo((state: defaultSnapProps) => {
              return state.snapPoints[state.snapPoints.length - 1];
            });
          }}
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.600"
          opacity={overlayOpacity}
          transition="opacity 0.2s"
          pointerEvents={overlayOpacity > 0 ? "auto" : "none"}
        />
      )}
      <BottomSheet
        open
        draggable
        skipInitialTransition
        ref={sheetRef}
        blocking={false}
        expandOnContentDrag
        snapPoints={({ maxHeight }) => [
          (maxHeight / 10) * 9,
          400,
          (maxHeight / 10) * 3,
        ]}
        onSpringStart={(snap) => {}}
        onSpringEnd={(snap) => {}}
        className={`custom-bottom-sheet ${
          overlayOpacity === 0 ? "no-radius" : ""
        }`}
        style={{
          background: "transparent",
        }}
      >
        {showDetail && (
          <Flex
            sx={{
              padding: "12px 20px",
              position: "relative",
            }}
            align={"center"}
            flexDir={"column"}
          >
            <Image
              src={CloseIcon}
              sx={{
                position: "absolute",
                right: "20px",
                top: "18px",
                width: "16px",
                height: "16px",
              }}
              onClick={() => {
                setShowDetail(false);
              }}
            ></Image>
            <Text
              sx={{
                color: "#D8762B",
                textAlign: "center",
                fontSize: "24px",
                fontWeight: 700,
                width: "100%",
              }}
            >
              Activity
            </Text>
            <Text
              sx={{
                fontFamily: "DIN-Black",
                fontSize: "50px",
                fontWeight: 900,
                marginTop: "10px",
              }}
            >
              {detail?.orderType === OrderType.Withdraw ? "-" : "+"}
              {formatAmountWithFixed(
                usdToCurrency(
                  accAdd(
                    accAdd(detail?.orderAmount, detail?.taxAmount),
                    detail?.tipAmount
                  )
                ),
                usdcInfo.decimals,
                2
              )}
            </Text>
            <Text
              sx={{
                color: "#333",
                fontFamily: "DIN",
                fontSize: "32px",
                fontWeight: 700,
              }}
            >
              {currencyUnit}
            </Text>
            <Box
              sx={{
                width: "100%",
                marginTop: "20px",
              }}
            >
              <Text
                sx={{
                  color: "#848484",
                  fontSize: "14px",
                  fontWeight: 700,
                }}
              >
                {detail?.orderType === OrderType.Withdraw
                  ? "Withdraw To"
                  : "From"}
              </Text>
              <Flex
                sx={{
                  marginTop: "10px",
                }}
                align={"center"}
              >
                <Text
                  sx={{
                    fontSize: "30px",
                    marginRight: "10px",
                  }}
                >
                  {detail?.orderType === OrderType.Withdraw
                    ? unknownEmoji
                    : consumerEmoji}
                </Text>
                <Text
                  sx={{
                    color: "#333",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  {shortenAddress(detail?.sender?.address ?? "")}
                </Text>
                <Text
                  sx={{
                    color: "#333",
                    textAlign: "right",
                    fontSize: "14px",
                    fontWeight: 500,
                    marginLeft: "auto",
                  }}
                >
                  {dayjs(detail?.createdAt).format("YYYY/MM/DD HH:mm")}
                </Text>
              </Flex>
              <Flex
                sx={{
                  marginTop: "15px",
                }}
              >
                <Text
                  sx={{
                    color: "#848484",
                    fontSize: "14px",
                    fontWeight: 700,
                  }}
                >
                  Total
                </Text>
                <Box
                  sx={{
                    marginLeft: "auto",
                    textAlign: "right",
                    fontSize: "14px",
                  }}
                >
                  <Text
                    sx={{
                      fontSize: "14px",
                      fontWeight: 700,
                    }}
                  >
                    {detail?.orderType === OrderType.Withdraw ? "-" : "+"}
                    {formatAmountWithFixed(
                      usdToCurrency(
                        accAdd(
                          accAdd(detail?.orderAmount, detail?.taxAmount),
                          detail?.tipAmount
                        )
                      ),
                      usdcInfo.decimals,
                      2
                    )}{" "}
                    {currencyUnit}
                  </Text>

                  <Text
                    sx={{
                      fontSize: "12px",
                    }}
                  >
                    {detail?.orderType === OrderType.Withdraw ? "-" : "+"}
                    {formatAmountWithFixed(
                      accAdd(
                        accAdd(detail?.orderAmount, detail?.taxAmount),
                        detail?.tipAmount
                      ),
                      usdcInfo.decimals
                    )}{" "}
                    {usdcInfo.symbol}
                  </Text>
                </Box>
              </Flex>
              <LogsButton
                sx={{
                  fontSize: "24px",
                  marginTop: "27px",
                  fontWeight: 700,
                }}
                onClick={() => {
                  if (detail?.orderType === OrderType.Withdraw) {
                    navigate(`/withdraw/receipt/${detail?.id}`);
                  } else {
                    navigate(`/receipt/${detail?.id}`);
                  }
                }}
              >
                View Receipt
              </LogsButton>
            </Box>
          </Flex>
        )}
        {!showDetail && (
          <Box
            sx={{
              padding: "12px 20px",
              height: "100%",
            }}
          >
            <Flex align="center" justify="space-between">
              <Text
                sx={{
                  color: "#D8762B",
                  fontSize: "18px",
                  fontWeight: 700,
                  textAlign: "center",
                  width: "100%",
                }}
              >
                Activity
              </Text>
            </Flex>

            {list.map((item: Order, index) => {
              const isWithdraw = item.orderType === "withdraw";
              const isCompleted = item.status === Status.Completed;
              return (
                <Flex
                  onClick={() => handleGoReceipt(item)}
                  key={index}
                  align={"center"}
                  sx={{
                    borderBottom: "1px solid #DEDEDE",
                    padding: "10px 0",
                  }}
                >
                  <Text
                    sx={{
                      fontSize: "30px",
                    }}
                  >
                    {isWithdraw ? unknownEmoji : consumerEmoji}
                  </Text>
                  <Box sx={{ marginLeft: "10px" }}>
                    {isWithdraw && (
                      <Text
                        sx={{
                          color: "#333",
                          fontSize: "14px",
                          fontWeight: 500,
                          position: "relative",
                        }}
                      >
                        Withdraw to
                      </Text>
                    )}
                    <Text
                      sx={{
                        color: "#848484",
                        fontSize: "12px",
                        fontStyle: "normal",
                        fontWeight: 500,
                      }}
                    >
                      {isWithdraw
                        ? shortenAddress(item.recipient.address)
                        : shortenAddress(item.sender.address)}
                    </Text>
                  </Box>

                  <Box
                    sx={{
                      marginLeft: "auto",
                    }}
                  >
                    <Box
                      sx={{
                        color: isCompleted ? "#00A54D" : "#BBB",
                        textAlign: "right",
                        fontSize: "16px",
                        fontWeight: 700,
                      }}
                    >
                      {showBalance ? (
                        <Text>
                          <span style={{ fontWeight: 700 }}>
                            {isWithdraw ? "-" : "+"}
                            {`${formatAmountWithFixed(
                              usdToCurrency(
                                accAdd(
                                  accAdd(item.orderAmount, item.taxAmount),
                                  item.tipAmount
                                )
                              ),
                              usdcInfo.decimals,
                              2
                            )}`}{" "}
                          </span>
                          <span style={{ fontSize: "12px", fontWeight: 700 }}>
                            {currencyUnit}
                          </span>
                        </Text>
                      ) : (
                        "******"
                      )}
                    </Box>
                    <Text
                      sx={{
                        color: isCompleted ? "#333" : "#D8762B",
                        textAlign: "right",
                        fontSize: "14px",
                        fontWeight: 500,
                        fontStyle: "italic",
                      }}
                    >
                      {isCompleted
                        ? dayjs(item.createdAt).fromNow()
                        : "Pending"}
                    </Text>
                  </Box>
                </Flex>
              );
            })}

            {list.length === 0 && (
              <Flex
                justify={"center"}
                align={"center"}
                flexDirection={"column"}
              >
                <Image
                  src={NoDataIcon}
                  sx={{
                    height: "60px",
                    marginTop: "10vh",
                  }}
                ></Image>
                <Text
                  sx={{
                    color: "#D8762B",
                    textAlign: "center",
                    fontSize: "16px",
                    fontWeight: 700,
                    marginTop: "10px",
                  }}
                >
                  Nothing here yet
                </Text>
              </Flex>
            )}
          </Box>
        )}
      </BottomSheet>
    </Box>
  );
};

export default Activity;
